/* eslint-disable @typescript-eslint/no-unused-expressions */

import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import type {JobFile} from '../../types'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {deleteIfEmpty, getJob} from './EditPipelinePage.slices.utils'

export const setFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    index: number
    file: JobFile
  }>
> = (state, action) => {
  const {pipelineId, jobId, index, file} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    job['files-publication'] ??= []
    job['files-publication'][index] = file
  }
}

export const addFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string}>
> = (state, action) => {
  const {pipelineId, jobId} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    job['files-publication'] ??= []
    job['files-publication'].push({
      path: '',
      'share-with-jobs': true,
      'publish-artifact': false,
    })
    const jobFilesPublicationLength = job['files-publication']?.length
    const jobFilesPublicationLengthIndex = jobFilesPublicationLength - 1
    const indexKey = `${jobId}_${jobFilesPublicationLengthIndex}`

    state[pipelineId]!.added ??= {}
    state[pipelineId]!.added!.filesPublication ??= {}
    state[pipelineId]!.added!.filesPublication![indexKey] = true
  }
}

export const deleteFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    index: number
  }>
> = (state, action) => {
  const {pipelineId, jobId, index} = action.payload
  const job = getJob(state, pipelineId, jobId)
  const jobFilesPublication = job?.['files-publication']
  const indexKey = `${jobId}_${index}`
  const addedFilesPublication = state[pipelineId]?.added?.filesPublication

  if (jobFilesPublication && addedFilesPublication?.[indexKey]) {
    job['files-publication'] = jobFilesPublication.filter((_, fileIndex) => fileIndex !== index)

    state[pipelineId]!.added!.filesPublication = job['files-publication'].reduce<
      Record<string, boolean>
    >((acc, _, fileIndex) => {
      acc[`${jobId}_${fileIndex}`] = true

      return acc
    }, {})

    deleteIfEmpty(state[pipelineId]!.added, 'filesPublication') &&
      deleteIfEmpty(state[pipelineId], 'added')
  } else {
    state[pipelineId] ??= {}
    state[pipelineId]!.deleted ??= {}
    state[pipelineId]!.deleted!.filesPublication ??= []
    state[pipelineId]!.deleted!.filesPublication!.push(indexKey)
  }
}

export const restoreFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; index: number}>
> = (state, action) => {
  const {pipelineId, jobId, index} = action.payload
  const {deleted} = state[pipelineId] ?? {}
  const indexKey = `${jobId}_${index}`

  if (deleted?.filesPublication?.includes(indexKey)) {
    deleted.filesPublication = deleted?.filesPublication.filter(item => item !== indexKey)
    deleteIfEmpty(deleted, 'filesPublication') && deleteIfEmpty(state[pipelineId], 'deleted')
  }
}

export const clearFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string}>
> = (state, action) => {
  const {pipelineId, jobId} = action.payload
  const {added} = state[pipelineId] ?? {}
  const job = getJob(state, pipelineId, jobId)

  if (added?.filesPublication != null && job != null) {
    delete added.filesPublication
    deleteIfEmpty(state[pipelineId], 'added')
  }
}

export const multiplyDeleteFilesPublication: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    indexes: number[]
  }>
> = (state, action) => {
  const {pipelineId, jobId, indexes} = action.payload
  const job = getJob(state, pipelineId, jobId)
  const jobFilesPublication = job?.['files-publication']

  if (jobFilesPublication) {
    job['files-publication'] = jobFilesPublication.filter(
      (_, fileIndex) => !indexes.includes(fileIndex),
    )

    if (state[pipelineId]?.added?.filesPublication != null) {
      state[pipelineId].added.filesPublication ??= job['files-publication'].reduce<
        Record<string, boolean>
      >((acc, _, fileIndex) => {
        acc[`${jobId}_${fileIndex}`] = true

        return acc
      }, {})
    }

    deleteIfEmpty(state[pipelineId]!.added, 'filesPublication') &&
      deleteIfEmpty(state[pipelineId], 'added')
  }
}

export const applyAddedFilesPublication: CaseReducer<PipelineDraftState, PayloadAction<string>> = (
  state,
  action,
) => {
  const pipelineId = action.payload
  const {added} = state[pipelineId] ?? {}

  if (added?.filesPublication != null) {
    delete added.filesPublication
    deleteIfEmpty(state[pipelineId], 'added')
  }
}
